import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { BrandModel } from '@/models/tables/brand.model';
import { BiggerDiscountService } from '@services/bigger-discount.service';

@Component({
  selector: 'app-bigger-discount',
  templateUrl: './bigger-discount.component.html',
  styleUrls: ['./bigger-discount.component.scss'],
})
export class BiggerDiscountComponent implements OnInit {
  triggerDate$ = new Subject<any>();
  reloadTrigger$ = new Subject<any>();
  protected readonly data = {};

  // Subjects per i dropdown
  triggerYear$ = new Subject<any>();
  triggerQuarter$ = new Subject<any>();
  triggerMonth$ = new Subject<any>();

  // Subjects per aggiornare i componenti
  triggerUpdateYear$ = new Subject<any>();
  triggerUpdateQuarter$ = new Subject<any>();
  triggerUpdateMonth$ = new Subject<any>();

  // Valori selezionati
  selectedYear: number | null = null;
  selectedQuarter: number | null = null;
  selectedMonth: number | null = null;

  // Metadati per i dropdown
  yearMeta = {
    label: 'Anno',
    options: [],
  };

  quarterMeta = {
    label: 'Trimestre',
    options: [
      { key: 1, value: 'I Trimestre' },
      { key: 2, value: 'II Trimestre' },
      { key: 3, value: 'III Trimestre' },
      { key: 4, value: 'IV Trimestre' },
    ],
  };

  monthMeta = {
    label: 'Mese',
    options: [],
  };

  brandsMeta = {
    label: 'Seleziona una marca',
    options: [],
    default: undefined as number | undefined,
  };
  brands: BrandModel[];
  brandIdSelected: number;
  triggerSelectionBrand$ = new Subject<any>();
  triggerUpdateComponentSelectBrand$ = new Subject<any>();

  constructor(private biggerDiscountService: BiggerDiscountService) {
    // Inizializza gli anni (ultimi 10 anni + prossimi 2)
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 2; i++) {
      years.push({ key: i, value: i.toString() });
    }
    this.yearMeta.options = years;

    // Inizializza i mesi (con opzione "Tutti i mesi")
    const months = [
      { key: null, value: 'Tutti i mesi' },
      { key: 1, value: 'Gennaio' },
      { key: 2, value: 'Febbraio' },
      { key: 3, value: 'Marzo' },
      { key: 4, value: 'Aprile' },
      { key: 5, value: 'Maggio' },
      { key: 6, value: 'Giugno' },
      { key: 7, value: 'Luglio' },
      { key: 8, value: 'Agosto' },
      { key: 9, value: 'Settembre' },
      { key: 10, value: 'Ottobre' },
      { key: 11, value: 'Novembre' },
      { key: 12, value: 'Dicembre' },
    ];
    this.monthMeta.options = months;

    // Usa getAllBrands() come in table-ms per avere il formato corretto
    this.biggerDiscountService.getAllBrands().subscribe((brands) => {
      this.brands = brands;
      this.brandsMeta.options = [
        {
          key: null,
          value: 'Tutte le marche',
        },
        ...brands.map((brand) => ({
          key: brand.id,
          value: brand.descrizioneMarca,
        })),
      ];
      this.triggerUpdateComponentSelectBrand$.next();
    });

    this.triggerSelectionBrand$.subscribe((brandId: number) => {
      this.brandIdSelected = brandId;
    });
  }

  ngOnInit(): void {
    // Imposta valori di default (solo anno e trimestre corrente, mese NON selezionato)
    const today = new Date();
    this.selectedYear = today.getFullYear();
    this.selectedMonth = null; // Mese non selezionato di default
    this.selectedQuarter = Math.ceil((today.getMonth() + 1) / 3);
    this.brandIdSelected = null;

    // Imposta i valori di default nei metadati
    this.yearMeta['default'] = this.selectedYear;
    this.quarterMeta['default'] = this.selectedQuarter;

    // Filtra i mesi in base al trimestre
    this.filterMonthsByQuarter(this.selectedQuarter);

    // Imposta il valore di default del mese a null (Nessun mese)
    this.monthMeta['default'] = null;
    this.brandsMeta['default'] = null;

    // Sottoscrivi ai cambiamenti PRIMA di emettere i valori iniziali
    this.triggerYear$.subscribe((year) => {
      this.selectedYear = year;
      this.emitDate();
    });

    this.triggerQuarter$.subscribe((quarter) => {
      this.selectedQuarter = quarter;
      // Filtra i mesi in base al trimestre selezionato
      this.filterMonthsByQuarter(quarter);
      this.emitDate();
    });

    this.triggerMonth$.subscribe((month) => {
      // Se viene selezionato null (Nessun mese), resetta il mese
      this.selectedMonth = month === null ? null : month;
      this.emitDate();
    });

    this.triggerSelectionBrand$.subscribe((marcaId) => {
      this.brandIdSelected = marcaId;
      this.emitDate();
    });

    // Emetti gli eventi di aggiornamento per inizializzare i dropdown
    setTimeout(() => {
      this.triggerUpdateYear$.next();
      this.triggerUpdateQuarter$.next();
      this.triggerUpdateMonth$.next();
      this.triggerUpdateComponentSelectBrand$.next();
      this.emitDate();
    }, 200);
  }

  filterMonthsByQuarter(quarter: number | null): void {
    const allMonths = [
      { key: 1, value: 'Gennaio' },
      { key: 2, value: 'Febbraio' },
      { key: 3, value: 'Marzo' },
      { key: 4, value: 'Aprile' },
      { key: 5, value: 'Maggio' },
      { key: 6, value: 'Giugno' },
      { key: 7, value: 'Luglio' },
      { key: 8, value: 'Agosto' },
      { key: 9, value: 'Settembre' },
      { key: 10, value: 'Ottobre' },
      { key: 11, value: 'Novembre' },
      { key: 12, value: 'Dicembre' },
    ];

    if (!quarter) {
      // Se non c'è trimestre, mostra tutti i mesi + opzione "Nessun mese"
      this.monthMeta.options = [{ key: null, value: 'Tutti i mesi' }, ...allMonths];
      return;
    }

    // Filtra i mesi in base al trimestre
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = quarter * 3;

    const filteredMonths = allMonths.filter(
      (month) => month.key >= startMonth && month.key <= endMonth,
    );

    // Aggiungi sempre l'opzione "Nessun mese" all'inizio
    this.monthMeta.options = [{ key: null, value: 'Tutti i mesi' }, ...filteredMonths];

    // Se il mese selezionato non è nel trimestre, resettalo (non selezionare automaticamente)
    if (this.selectedMonth && (this.selectedMonth < startMonth || this.selectedMonth > endMonth)) {
      this.selectedMonth = null;
      delete this.monthMeta['default'];
      // Aggiorna il dropdown del mese per resettare la selezione
      setTimeout(() => {
        this.triggerUpdateMonth$.next();
      }, 50);
    }
  }

  emitDate(): void {
    // Emetti se abbiamo almeno l'anno
    if (this.selectedYear) {
      const payload: any = {
        year: this.selectedYear,
      };

      // Se il mese è selezionato, includilo
      if (this.selectedMonth) {
        payload.month = this.selectedMonth;
      }

      // Se il trimestre è selezionato, includilo (soprattutto se il mese non è selezionato)
      if (this.selectedQuarter) {
        payload.quarter = this.selectedQuarter;
      }

      // Se la marca è selezionata, includila
      if (this.brandIdSelected) {
        payload.brandId = this.brandIdSelected;
      }

      this.triggerDate$.next(payload);
    }
  }
}
